import { Model } from '@/models'
import { AccountStatementReportService } from '@/services'
import { formatDate } from '@/helpers/date'
import { AccountStatementReportActions } from '@/enums'
import { QueueItemStatus } from '@/enums/graphql'
import { i18n } from '@/i18n'
import { textList } from '@/helpers'

export class AccountStatementReport extends Model {
    /**
     * Create a AccountStatementReport model wrapper.
     * @param {Object} data - Object holding the field values.
     */
    constructor (data) {
        super()
        Model.initializeFields(this, [
            'id',
            'owner',
            'accounts',
            'account_number_from',
            'account_number_to',
            'date_from',
            'date_until',
            'file_types',
            'currentQueueItem',
            'queueItems',
            'fileStorageItem',
        ], data)
    }

    static get service () { return AccountStatementReportService }
    static action = AccountStatementReportActions

    /**
     * Add custom actions.
     * @returns {Array}
     */

    get actions () {
        const actions = super.actions

        // Cancel creation
        if (this.currentQueueItem.status === QueueItemStatus.QUEUED) {
            actions.push({
                key: AccountStatementReportActions.CANCEL_CREATION,
                item: this,
            })
        }

        // Delete
        if ([QueueItemStatus.FAILED, QueueItemStatus.CANCELLED, QueueItemStatus.DONE].includes(this.currentQueueItem.status)) {
            actions.push({
                key: AccountStatementReportActions.DELETE,
                item: this,
            })
        }

        return actions
    }

    /**
     * Create a new accountStatementReport.
     * @param {Object} accountStatementReport - Typeof AccountStatementReportInput.
     * @returns {Promise<*>}
     */

    static create (accountStatementReport) {
        return AccountStatementReportService.create({ accountStatementReport: accountStatementReport })
    }

    /**
     * Stops already queued `AccountStatementReport` from being created.
     * @param {Array} ids
     * @returns {Promise<*>}
     */
    static cancelCreation (ids) {
        const variables = {
            account_statement_report_ids: ids,

        }
        return AccountStatementReportService.cancelCreation(variables)
    }

    /**
     * Stops this already queued `AccountStatementReport` from being created.
     * @returns {Promise<*>}
     */
    cancelCreation () {
        return AccountStatementReport.cancelCreation([this.id])
    }

    /**
     * Deletes `AccountStatementReports`.
     * @param {Array} ids
     * @returns {Promise<*>}
     */
    static delete (ids) {
        const variables = {
            account_statement_report_ids: ids,
        }
        return AccountStatementReportService.delete(variables)
    }

    get formattedDateFrom () {
        return formatDate(this.date_from)
    }

    get formattedDateUntil () {
        return formatDate(this.date_until)
    }

    get formattedPeriod () {
        return `${this.formattedDateFrom}–${this.formattedDateUntil}`
    }

    // TODO improvement: Extract to helper function.
    get formattedAccountList () {
        const threshold = 3
        let accountList = {}

        // Individual accounts
        if (this.accounts?.length) {
            // TODO @TFU: Check if it would make more sense to already get the accounts sorted correctly (by `account.number` ASC) from the API.
            // Get account numbers (Integers) from array and sort them
            accountList.accounts = this.accounts.map(account => account.number).sort(function(a, b) { return a - b })

            if (this.accounts.length > threshold) {
                accountList.teaser = {
                    full: `${accountList.accounts.slice(0, threshold).join(', ')} … ${i18n.t('common.term.more--and-n-other--entity', { amount: this.accounts.length - threshold, entity: i18n.tc('common.accounting.account.account', 2) })}`,
                    short: `${accountList.accounts.slice(0, threshold).join(', ')} … ${i18n.t('common.term.more--and-n-others', { amount: this.accounts.length - threshold })}`,
                    accounts: accountList.accounts.slice(0, threshold).join(', '),
                    numberOfAdditionalAccounts: this.accounts.length - threshold,
                }
            }

            if (this.accounts.length === 1) {
                accountList.accounts = `${this.accounts[0].number} – ${this.accounts[0].name}`
            } else {
                accountList.accounts = textList(accountList.accounts)
            }
        } else {
            // Account range
            accountList.accounts = i18n.tc('common.term.from-x-to-y--short', 1, { x: this.account_number_from, y: this.account_number_to})
        }

        return accountList
    }
}
